import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/solid';
import { Button, Toggle } from '@components/base';
import React, { useRef, useState } from 'react';
import Navlink from 'next/link';
import {
    useUniversalModalDispatch,
    useUniversalModalState,
} from '../context/UniversalModalContext';
import { Form, Formik } from 'formik';
import { FormikWrapped } from '@components/form/FormikWrapped';
import {
    useCreatePermissionLevelMutation,
    useDeletePermissionLevelMutation,
    useUpdatePermissionLevelMutation,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';

export function UniversalModal() {
    const { open, component } = useUniversalModalState();
    const { toggle } = useUniversalModalDispatch();
    const focusRef = useRef<HTMLDivElement | null>(null);
    return (
        <Transition show={open}>
            <Dialog
                as="div"
                static
                open={open}
                initialFocus={focusRef}
                onClose={() => {
                    toggle(false);
                }}
                className="fixed group inset-0 z-1001">
                <div className="absolute inset-0" ref={focusRef}>
                    <Dialog.Overlay className="absolute inset-0 bg-black opacity-50" />
                    {component && component}
                </div>
            </Dialog>
        </Transition>
    );
}

export function Notifications() {
    const { toggle } = useUniversalModalDispatch();
    return (
        <div className="fixed inset-0 flex overflow-y-auto h-full">
            <div className="relative flex-grow flex-1 flex justify-center items-center py-2">
                <div className="bg-white rounded-md ">
                    <div className="flex justify-between items-center border-b border-gray-300 px-4">
                        <div className="py-4 mr-52">
                            <h1 className="text-2xl">Notification Settings</h1>
                        </div>
                        <div>
                            <XIcon
                                className="h-8 w-8"
                                onClick={() => {
                                    toggle(false);
                                }}
                            />
                        </div>
                    </div>
                    <div className="py-2 px-4">
                        <div className="py-2">Choose the notifications you want to receive:</div>
                        <div>
                            <h2 className="my-2">COURSE ACTIVITES</h2>
                            <ToggleRow text="Course Membership Status" />
                            <ToggleRow text="Student Requests Help" />
                            <ToggleRow text="Activity is Late" />
                            <ToggleRow text="Activity Reassigned to Student" />
                        </div>
                        <div className="mb-4">
                            <h2 className="my-2">AGENDA</h2>
                            <ToggleRow text="Event Reminder" />
                            <ToggleRow text="New Calendar Event" />
                            <ToggleRow text="Event Time / Date Change" />
                            <ToggleRow text="Event Cancelled" />
                        </div>
                        <Navlink href="/users/notifications">
                            <div className="py-2 text-teal-600 hover:text-teal-800 cursor-pointer">
                                <p className="text-base">Additional Notification Settings</p>
                            </div>
                        </Navlink>
                    </div>
                    <div className="flex w-full border-t border-gray-300 p-4">
                        <Button
                            variant="secondary"
                            className="flex w-full items-center justify-center">
                            Save settings
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ToggleRow({ text }: { text: string }) {
    const [enabled, setEnabled] = useState(false);
    return (
        <div className="flex py-2">
            <div className="mr-3">
                <Toggle enabled={enabled} type="button" setEnabled={setEnabled} />
            </div>
            <p>{text}</p>
        </div>
    );
}

const valSchema = yup.object().shape({
    name: yup.string().required('Permission name is required'),
});

export function AddPermissionModal() {
    const { toggle } = useUniversalModalDispatch();
    const queryClient = useQueryClient();
    const mutate = useCreatePermissionLevelMutation(getClient('users'), {
        onSuccess: (data) => {
            if (data?.createPermissionLevel?.success) {
                queryClient.invalidateQueries(['permissionLevels']);
                queryClient.invalidateQueries(['permissionsWithUsers']);
                toggle(false);
            }
        },
    });
    return (
        <div className="fixed inset-0 overflow-y-auto flex h-full">
            <div className="relative flex-grow flex-1 flex justify-center items-center py-2">
                <div className="relative bg-white rounded-md  overflow-visible ">
                    <div>
                        <div className="overflow-visible justify-between items-center border-b border-gray-300 px-4 ">
                            <div className="flex w-full h-full justify-between items-center">
                                <div className="py-4 mr-52">
                                    <h1 className="text-2xl text-passport inline-block whitespace-nowrap">
                                        Create New Permission Level
                                    </h1>
                                </div>
                                <div className="">
                                    <XIcon
                                        className="h-8 w-8"
                                        onClick={() => {
                                            toggle(false);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <Formik
                            initialValues={{ name: '' }}
                            validationSchema={valSchema}
                            onSubmit={({ name }) => {
                                mutate.mutate({ name });
                            }}>
                            <Form>
                                <div className="py-6 px-4 flex-1 flex flex-col h-full">
                                    <FormikWrapped
                                        labelText="Permission Level Name"
                                        placeholder="Enter permission level name"
                                        className="mb-6"
                                        labelClassName="text-gray-500 text-base pb-2"
                                        id="name"
                                        name="name"
                                    />
                                </div>
                                <div className="flex w-full border-t border-gray-300 p-4">
                                    <Button
                                        type="submit"
                                        disabled={false}
                                        variant="secondary"
                                        className="w-full flex justify-center items-center">
                                        {mutate.isLoading ? 'CREATING...' : 'ADD PERMISSION LEVEL'}
                                    </Button>
                                </div>
                            </Form>
                        </Formik>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function UpdatePermissionName({ id, intialValue }: { id: number; intialValue: string }) {
    const { toggle, dispatchNotification } = useUniversalModalDispatch();
    const queryClient = useQueryClient();
    const mutate = useUpdatePermissionLevelMutation(getClient('users'), {
        onSuccess: (data) => {
            if (data?.updatePermissionLevel?.success) {
                queryClient.invalidateQueries(['permissionLevels']);
                queryClient.invalidateQueries(['permissionsWithUsers']);
                toggle(false);
            }
        },
    });

    const deleteMutate = useDeletePermissionLevelMutation(getClient('users'), {
        onSuccess: (data) => {
            if (data?.deletePermissionLevel?.success) {
                queryClient.invalidateQueries(['permissionLevels']);
                queryClient.invalidateQueries(['permissionsWithUsers']);
                toggle(false);
            } else {
                const error =
                    data?.deletePermissionLevel?.errors?.[0] ??
                    'Something went wrong however no error was provided.';
                dispatchNotification({
                    title: 'Failed to Delete Permission',
                    text: error,
                    type: 'danger',
                });
            }
        },
    });

    return (
        <div className="fixed inset-0 overflow-y-auto flex h-full">
            <div className="relative flex-grow flex-1 flex justify-center items-center py-2">
                <div className="relative bg-white rounded-md overflow-visible ">
                    <div>
                        <div className="overflow-visible justify-between items-center border-b border-gray-300 px-4 ">
                            <div className="flex w-full h-full justify-between items-center">
                                <div className="py-4 mr-64">
                                    <h1 className="text-2xl text-passport inline-block whitespace-nowrap">
                                        Edit Permission Level
                                    </h1>
                                </div>
                                <div className="">
                                    <XIcon
                                        className="h-8 w-8"
                                        onClick={() => {
                                            toggle(false);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <Formik
                            initialValues={{ name: intialValue }}
                            validationSchema={valSchema}
                            onSubmit={({ name }) => {
                                mutate.mutate({ name, id });
                            }}>
                            <Form>
                                <div className="py-6 px-4 flex-1 flex flex-col h-full">
                                    <FormikWrapped
                                        labelText="Permission Level Name"
                                        placeholder="Enter permission level name"
                                        className="mb-6"
                                        labelClassName="text-gray-500 text-base pb-2"
                                        id="name"
                                        name="name"
                                    />
                                </div>
                                <div className="flex w-full border-t border-gray-300 p-4">
                                    <div className="flex flex-1 justify-between">
                                        <div className="">
                                            <Button
                                                type="button"
                                                onClick={() => {
                                                    deleteMutate.mutate({ id });
                                                }}
                                                disabled={false}
                                                variant="white"
                                                className="w-full flex justify-center items-center">
                                                {deleteMutate.isLoading ? 'DELETING...' : 'DELETE'}
                                            </Button>
                                        </div>
                                        <div className="">
                                            <Button
                                                type="submit"
                                                disabled={false}
                                                variant="secondary"
                                                className="w-full flex justify-center items-center">
                                                {mutate.isLoading
                                                    ? 'SAVING...'
                                                    : 'SAVE PERMISSION LEVEL'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        </Formik>
                    </div>
                </div>
            </div>
        </div>
    );
}
